<template>
  <div class="text-area-wrapper no-v-short-key">
    <div
      :class="[
        'text-area',
        $attrs['parent-class'],
        bgColor,
        size,
        borderColor,
        {
          active: !!value,
          focus: isFocus,
          'has-error': hasError,
        },
      ]"
    >
      <textarea
        :class="['px-4 text-area-input', $attrs['input-class'], hasShortKey]"
        :value="value"
        :placeholder="placeholder"
        v-bind="excludeAttrs"
        :maxlength="maxLength"
        :minlength="minLength"
        @focus="(isFocus = true), $emit('emitFocus');"
        @blur="(isFocus = false), $emit('emitBlur');"
        v-on="$listeners"
        @input="setValue($event.target.value)"
      />
      <span class="text-area-label">
        {{ label }}
      </span>
      <div class="text-area-icon icon-left">
        <slot name="icon-left" />
      </div>
    </div>
    <span
      v-if="!isHiddenError && (serverError || error)"
      class="text-area-error"
    >
      <template v-if="!serverError && error">
        {{ $t(`forms.${$attrs.name}.errors.${error}`) }}
      </template>
      <template v-if="serverError">
        {{ serverError }}
      </template>
    </span>
    <div
      v-if="helper"
      class="text-area-helper"
      v-html="helper"
    />
  </div>
</template>

<script>
import { enToFa } from '@helper/convert-digit'

export default {
  name: 'AppTextarea',
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'update'
  },
  props: {
    autoFocus: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    helper: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number],
      default: ''
    },
    maxLength: {
      type: [String, Number],
      default: ''
    },
    minLength: {
      type: [String, Number],
      default: ''
    },
    error: {
      type: String,
      default: null
    },
    serverError: {
      type: String,
      default: null
    },
    checkNumber: {
      type: Boolean,
      default: true
    },
    isShortKey: {
      type: Boolean,
      default: false
    },
    isHiddenError: {
      type: Boolean,
      default: false
    },
    bgColor: {
      type: String,
      default: 'bg-white'
    },
    size: {
      type: String,
      default: 'h-24'
    },
    borderColor: {
      type: String,
      default: 'border-gray-light'
    }
  },
  data () {
    return {
      isFocus: false
    }
  },
  computed: {
    hasShortKey () {
      return !this.isShortKey ? 'no-v-short-key' : ''
    },
    hasError () {
      return !!(this.error || this.serverError)
    },
    excludeAttrs () {
      const attr = { ...this.$attrs }
      delete attr['input-class']
      return attr
    }
  },
  mounted () {
    if (this.autoFocus) {
      this.focus()
    }
  },
  methods: {
    setValue (value) {
      if (this.checkNumber) {
        this.$emit('update', enToFa(value))
      } else {
        this.$emit('update', value)
      }
    },
    focus () {
      const thisInput = document.querySelector(
        `input[name=${this.$attrs.name}]`
      )
      thisInput.focus()
      this.isFocus = true
    },
    unFocus () {
      const thisInput = document.querySelector(
        `input[name=${this.$attrs.name}]`
      )
      thisInput.blur()
      this.isFocus = false
    }
  }
}
</script>
<!-- xs sm md lg xl 2xl -->
<style lang="scss" scoped>
.text-area {
  @apply w-full block relative flex items-center rounded border;
  transition: height 0.2s ease-in-out;

  &.focus {
    @apply border-blue-primary;
  }

  &.has-error,
  &.has-error.focus {
    @apply border-red-500;
  }

  &.active {
    .text-area {
      &-label {
        @apply -top-5 -right-4 translate-y-0 text-xs-xs;
      }
    }
  }

  &-input {
    @apply py-3 h-full rounded w-full border-none focus:outline-none  text-gray-main text-xs xs:text-sm md:text-sm resize-none;
    &__wrapper {
      @apply flex items-center;
    }

    &[readonly] {
      @apply cursor-pointer;
    }

    &[disabled] {
      @apply bg-white;
      + {
        .text-area-label {
          @apply text-gray-light;
        }
      }
    }
  }

  &-error {
    @apply text-red-500 text-xs py-1 px-1 font-normal pb-4 block;
  }

  &-label {
    @apply absolute top-1/2 transform -translate-y-1/2 pr-4 text-gray-medium text-sm font-medium pointer-events-none transition-all;
  }

  &-helper {
    @apply text-gray-medium text-xs px-4 py-1 font-normal;
  }
}
</style>
